import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import Modal from '../components/Modal';
import Spinner from '../components/Spinner';
import {
  cancelMyHostedMeetingSeries,
  cancelMyHostedMeeting,
  correctAttendanceAsStaff,
  createAttendanceVerification,
  deleteMyHostedMeetingIfEmpty,
  findUserDirectoryUser,
  getDepartments,
  getMyHostedMeetingState,
  getMyMeetingState,
  getMyOperationalMeetingState,
  manualCheckInExpectedAttendee,
  manualCheckInMyHostedMeeting,
  removeExpectedAttendee,
  removeMeetingCohost,
  setExpectedAttendeeDisposition,
  searchUserDirectory,
} from '../lib/api';
import { downloadCsvDocument } from '../lib/csvExport';
import { storage } from '../lib/storage';
import {
  errorMessage,
  formatDate,
  formatDateTime,
  formatMalaysiaIsoTime,
  formatTime,
  isMeetingToday,
  isMtibEmail,
  isValidEmail,
  canPrintAttendance,
  meetingDayState,
  normalizeProfileText,
} from '../lib/utils';
import type {
  AttendanceRecord,
  DirectoryUser,
  ExpectedAttendee,
  HostMeetingState,
  MeetingPublic,
  OperationalMeetingState,
  ParticipantRosterRow,
  SafeCohostDisplay,
  SafeUserMeetingState,
  Department,
  AttendanceVerificationPrintDocument,
} from '../types';
import { useToast } from '../context/ToastContext';
import { useRealtimeSignal } from '../hooks/useRealtimeSignal';
import { generateBrandedQrDataUrl } from '../lib/qr';
import MeetingRelationBadge from '../components/MeetingRelationBadge';
import { buildHostRoster, type DisplayRosterRow } from '../lib/attendanceRoster';
import { meetingActionPolicy, meetingLifecycleLabel, resolveMeetingLifecycle } from '../lib/meetingActionPolicy';
import { formatOfficialAttendanceStatus } from '../lib/officialAttendanceDocument';

const rosterCollator = new Intl.Collator('ms-MY', { sensitivity: 'base', numeric: true });
const rosterStatusOrder: Record<DisplayRosterRow['status'], number> = { Pending: 0, 'Checked In': 1, 'Not Coming': 2, Invalidated: 3 };

function rosterStatusLabel(status: DisplayRosterRow['status']): string {
  return status === 'Checked In' ? 'Telah Hadir' : status === 'Not Coming' ? 'Tidak Hadir' : status === 'Invalidated' ? 'Kehadiran Dibatalkan' : 'Belum Hadir';
}

function rosterCategoryLabel(category: DisplayRosterRow['category']): string {
  return category === 'Internal' ? 'Dalaman' : category === 'Guest' ? 'Tetamu' : 'Tidak diketahui';
}

function rosterMethodLabel(method: DisplayRosterRow['method']): string {
  return method === 'QR' ? 'QR' : method === 'Manual' ? 'Manual' : method === 'Legacy' ? 'Rekod lama' : '—';
}

function sortRoster(rows: DisplayRosterRow[], mode: 'status' | 'recent'): DisplayRosterRow[] {
  return [...rows].sort((a, b) => {
    if (mode === 'recent') {
      const aTime = a.time && a.status !== 'Invalidated' ? Date.parse(a.time) : 0;
      const bTime = b.time && b.status !== 'Invalidated' ? Date.parse(b.time) : 0;
      if (aTime !== bTime) return bTime - aTime;
    } else {
      const statusOrder = rosterStatusOrder[a.status] - rosterStatusOrder[b.status];
      if (statusOrder) return statusOrder;
    }
    return rosterCollator.compare(a.display.trim(), b.display.trim());
  });
}

function buildParticipantRoster(rows: ParticipantRosterRow[]): DisplayRosterRow[] {
  return rows.map((row) => ({
    key: row.key,
    display: row.display_name,
    sub: row.department_org ?? 'Ahli dijemput',
    status: row.status,
    category: row.category,
    time: row.checkin_time,
    method: row.checkin_method,
    type: row.key.startsWith('attendance-') ? 'Additional' : 'Expected',
    attendeeEmail: null,
    departmentOrg: row.department_org,
  }));
}

export default function MeetingDetailPage() {
  const { meetingId = '' } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [userState, setUserState] = useState<SafeUserMeetingState | null>(null);
  const [hostState, setHostState] = useState<HostMeetingState | null>(null);
  const [operationalState, setOperationalState] = useState<OperationalMeetingState | null>(null);
  const [accessError, setAccessError] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [verificationQrDataUrl, setVerificationQrDataUrl] = useState('');
  const [verificationPrintDocument, setVerificationPrintDocument] = useState<AttendanceVerificationPrintDocument | null>(null);
  const [printBusy, setPrintBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [busy, setBusy] = useState(true);
  const [manualContext, setManualContext] = useState<{
    row?: DisplayRosterRow;
    registeredProfile?: DirectoryUser | null;
  } | null>(params.get('manual') === '1' ? {} : null);
  const [seriesAction, setSeriesAction] = useState<'edit' | 'cancel' | null>(null);
  const [invalidating, setInvalidating] = useState<AttendanceRecord | null>(null);
  const [search, setSearch] = useState('');
  const [tab, setTab] = useState<'All' | 'Internal' | 'Guest'>('All');
  const [sortMode, setSortMode] = useState<'status' | 'recent'>('recent');
  const [cohostRemoval, setCohostRemoval] = useState<{ email: string; expected: ExpectedAttendee | null; hasAttendanceHistory: boolean } | null>(null);
  const [expectedAction, setExpectedAction] = useState<{ row: DisplayRosterRow } | null>(null);
  const [cancelDialog, setCancelDialog] = useState<{ scope: 'occurrence' | 'series' } | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setBusy(true);
    try {
      const scoped = await getMyMeetingState(meetingId);
      setUserState(scoped);
      setAccessError('');
      const hasOperationalRole = scoped.viewer.relationship === 'HOST'
        || scoped.viewer.relationship === 'COHOST';
      const [nextHostState, peopleState] = await Promise.all([
        scoped.viewer.relationship === 'HOST' ? getMyHostedMeetingState(meetingId) : Promise.resolve(null),
        hasOperationalRole ? getMyOperationalMeetingState(meetingId).catch(() => null) : Promise.resolve(null),
      ]);
      setHostState(nextHostState);
      setOperationalState(peopleState);
    } catch (error) {
      const message = errorMessage(error);
      setAccessError(message);
      if (!silent) showToast(message, 'error');
    } finally {
      if (!silent) setBusy(false);
    }
  }, [meetingId, showToast]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const poll = window.setInterval(() => { if (!document.hidden) void load(true); }, 15000);
    return () => window.clearInterval(poll);
  }, [load]);
  useRealtimeSignal(useCallback(() => { void load(true); }, [load]), meetingId);

  const canManage = Boolean(userState?.viewer.is_host && hostState);
  const meeting = operationalState?.meeting ?? hostState?.meeting ?? userState?.meeting ?? null;
  const checkInOpen = Boolean(meeting && meeting.status === 'Active' && isMeetingToday(meeting.meeting_date));
  const lifecycle = meeting ? resolveMeetingLifecycle(meeting) : undefined;
  const actionPolicy = lifecycle ? meetingActionPolicy(lifecycle) : null;
  const canUseSelfCheckInQr = checkInOpen && Boolean(actionPolicy?.saveQr);
  const hasCheckedIn = Boolean(userState?.viewer.has_checked_in);
  const attendanceInvalidated = userState?.viewer.attendance_state === 'ATTENDANCE_INVALIDATED';
  const viewerHasRosterAccess = Boolean(userState && canPrintAttendance(userState.viewer));

  useEffect(() => {
    if ((!viewerHasRosterAccess && operationalState?.viewer_role !== 'COHOST') || !canUseSelfCheckInQr) {
      setQrDataUrl('');
      return;
    }
    const url = `${window.location.origin}/checkin/${meetingId}`;
    generateBrandedQrDataUrl(url, { width: 1000 }).then(setQrDataUrl).catch((error) => showToast(errorMessage(error), 'error'));
  }, [canUseSelfCheckInQr, meetingId, operationalState?.viewer_role, showToast, viewerHasRosterAccess]);

  const roster = useMemo(
    () => operationalState
      ? buildHostRoster(operationalState.expected, operationalState.attendance)
      : hostState
        ? buildHostRoster(hostState.expected, hostState.attendance)
        : buildParticipantRoster(userState?.roster ?? []),
    [hostState, operationalState, userState],
  );
  const filteredRoster = useMemo(() => {
    const q = search.trim().toLowerCase();
    return sortRoster(roster.filter((row) =>
      (tab === 'All' || row.category === tab)
      && (!q || row.display.toLowerCase().includes(q) || row.sub.toLowerCase().includes(q)),
    ), sortMode);
  }, [roster, search, sortMode, tab]);

  const attendanceForOperations = operationalState?.attendance ?? hostState?.attendance ?? [];
  const validAttendanceCount = attendanceForOperations.filter((record) => record.is_valid !== false).length;
  const invalidAttendanceCount = attendanceForOperations.filter((record) => record.is_valid === false).length;
  const canAttendanceExport = Boolean(actionPolicy?.exportCsv)
    && (viewerHasRosterAccess || operationalState?.viewer_role === 'COHOST');
  const canGenerateVerificationPrint = Boolean(actionPolicy?.printAttendance)
    && (userState?.viewer.relationship === 'HOST' || userState?.viewer.relationship === 'COHOST');
  const resetRosterFilters = () => {
    setSearch('');
    setTab('All');
    setSortMode('recent');
  };

  const openProjector = async () => {
    if (operationalState?.viewer_role === 'COHOST') {
      window.open(new URL(`/projector/${meetingId}`, window.location.origin).toString(), '_blank', 'noopener,noreferrer');
      return;
    }
    window.open(new URL(`/projector/${meetingId}`, window.location.origin).toString(), '_blank', 'noopener,noreferrer');
  };

  const saveQr = () => {
    if (!canUseSelfCheckInQr || !qrDataUrl || !meeting) return;
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = `MTIB-${meeting.title.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '')}-QR.png`;
    link.click();
  };

  const meetingTimeLabel = meeting ? formatTime(meeting.meeting_time) : '';

  const exportAttendanceCsv = () => {
    if (!meeting || !canAttendanceExport) return;
    const rows = [
      ['Nama Mesyuarat', meeting.title],
      ['Tarikh Mesyuarat', formatDate(meeting.meeting_date)],
      ['Waktu', meetingTimeLabel],
      ['Lokasi', meeting.location ?? ''],
      ...(meeting.chairman ? [['Pengerusi', meeting.chairman]] : []),
      [],
      ['Senarai Kehadiran'],
      [],
      ['Nama Ahli', 'Kategori', 'Kaedah', 'Bahagian / Organisasi', 'E-mel', 'Jenis', 'Status', 'Sebab', 'Nota Pembatalan', 'Waktu Check-in'],
      ...sortRoster(roster, 'status').map((row) => [
        row.display,
        rosterCategoryLabel(row.category),
        rosterMethodLabel(row.method),
        row.departmentOrg ?? '',
        row.attendeeEmail ?? '',
        row.type === 'Expected' ? 'Dijemput' : 'Tambahan',
        rosterStatusLabel(row.status),
        row.status === 'Not Coming'
          ? [row.reason, row.reasonDetail].filter(Boolean).join(' — ')
          : row.attendance?.corrections?.slice().reverse().find((correction) => correction.action === 'INVALIDATE')?.reason ?? '',
        row.attendance?.corrections?.slice().reverse().find((correction) => correction.action === 'INVALIDATE')?.note ?? '',
        row.time ? formatDateTime(row.time) : '',
      ]),
    ];
    const safeTitle = meeting.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 80) || 'meeting';
    downloadCsvDocument(`attendance-${meeting.meeting_date}-${safeTitle}.csv`, rows);
  };

  const prepareOfficialAttendanceDocument = async () => {
    const verification = await createAttendanceVerification(meetingId);
    if (!verification.print_document) throw new Error('Private print snapshot was not returned by the verification service.');
    const verificationUrl = new URL(
      `/attendance-verification/${encodeURIComponent(verification.token)}`,
      window.location.origin,
    ).toString();
    const nextVerificationQr = await generateBrandedQrDataUrl(verificationUrl, { width: 720 });
    return {
      printDocument: verification.print_document,
      verificationQrDataUrl: nextVerificationQr,
      verificationUrl,
    };
  };

  const exportAttendancePdf = async () => {
    if (!canGenerateVerificationPrint || pdfBusy) return;
    setPdfBusy(true);
    try {
      const officialDocument = await prepareOfficialAttendanceDocument();
      const { downloadAttendancePdf } = await import('../lib/attendancePdf');
      await downloadAttendancePdf(officialDocument);
    } catch (error) {
      showToast(`PDF tidak dapat disediakan. ${errorMessage(error)}`, 'error');
    } finally {
      setPdfBusy(false);
    }
  };

  const printAttendance = async () => {
    if (!canGenerateVerificationPrint || printBusy) return;
    setPrintBusy(true);
    try {
      const officialDocument = await prepareOfficialAttendanceDocument();

      flushSync(() => {
        setVerificationQrDataUrl(officialDocument.verificationQrDataUrl);
        setVerificationPrintDocument(officialDocument.printDocument);
      });
      window.setTimeout(() => window.print(), 0);
    } catch (error) {
      showToast(`Cetakan tidak dapat disediakan. ${errorMessage(error)}`, 'error');
    } finally {
      setPrintBusy(false);
    }
  };

  const cancel = (scope: 'occurrence' | 'series' = 'occurrence') => {
    const wholeSeries = scope === 'series' && Boolean(meeting?.series_id);
    setSeriesAction(null);
    setCancelDialog({ scope: wholeSeries ? 'series' : 'occurrence' });
  };

  const startEdit = () => {
    if (meeting?.series_id) setSeriesAction('edit');
    else navigate(`/meetings/${meetingId}/edit?scope=occurrence`);
  };

  const startCancel = () => {
    if (meeting?.series_id) setSeriesAction('cancel');
    else cancel();
  };

  const remove = async () => {
    if (!window.confirm('Padam mesyuarat ini? Gunakan tindakan ini hanya untuk rekod tersilap, ujian atau pendua yang tidak sepatutnya wujud.')) return;
    try {
      await deleteMyHostedMeetingIfEmpty(meetingId);
      storage.removeHostSecret(meetingId);
      storage.removeProjectorSecret(meetingId);
      showToast('Mesyuarat telah dipadam.', 'success');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      const message = errorMessage(error);
      showToast(/history|attendance|reservation|notification|cohost|expected/i.test(message)
        ? 'Mesyuarat ini mempunyai rekod operasi dan tidak boleh dipadam. Gunakan Batalkan Mesyuarat.'
        : message, 'error');
    }
  };

  const restoreAttendance = async (record: AttendanceRecord) => {
    if (!window.confirm(`Pulihkan rekod kehadiran ${record.snapshot_name}?`)) return;
    try {
      await correctAttendanceAsStaff({ recordId: record.record_id, action: 'RESTORE' });
      showToast('Kehadiran telah dipulihkan. Rekod asal tidak diubah.', 'success');
      await load(true);
    } catch (error) { showToast(errorMessage(error), 'error'); }
  };

  const removeExpected = async (row: DisplayRosterRow) => {
    if (!row.expected) return;
    if (row.status !== 'Pending') {
      showToast(row.status === 'Not Coming' ? 'Pulihkan ahli ini kepada Belum Hadir sebelum mengeluarkannya.' : 'Sejarah kehadiran melindungi rekod ini daripada dikeluarkan.', 'error');
      return;
    }
    if (!window.confirm(`Keluarkan ${row.display} daripada ahli dijangka hadir?`)) return;
    try {
      await removeExpectedAttendee(row.expected.expected_id);
      showToast('Ahli dijemput telah dikeluarkan.', 'success');
      await load(true);
    } catch (error) { showToast(errorMessage(error), 'error'); }
  };

  const startExpectedCheckIn = async (row: DisplayRosterRow) => {
    if (!row.expected || !actionPolicy?.expectedAttendeeMutation) return;
    const expectedValue = row.expected.name_or_email.trim();
    try {
      const registeredProfile = isValidEmail(expectedValue)
        ? await findUserDirectoryUser(expectedValue)
        : null;
      setManualContext({ row, registeredProfile });
    } catch (error) {
      showToast(errorMessage(error), 'error');
    }
  };

  if (busy && !meeting) return <Layout title="Mesyuarat"><Spinner label="Memuatkan mesyuarat…" /></Layout>;
  if (accessError || !meeting || !userState) {
    return <Layout title="Butiran Mesyuarat"><div className="empty-state"><h2>Mesyuarat tidak tersedia</h2><p>{accessError || 'Mesyuarat ini tidak dikaitkan dengan profil kakitangan anda.'}</p><button className="button button-primary" onClick={() => navigate('/dashboard')}>Kembali ke Utama</button></div></Layout>;
  }

  const dayState = meetingDayState(meeting.meeting_date);
  const isHost = userState.viewer.relationship === 'HOST';
  const isCohost = userState.viewer.relationship === 'COHOST';
  const canOperate = canManage || Boolean(isCohost && operationalState);
  const canViewAttendanceAudit = canOperate && Boolean(actionPolicy?.attendanceAuditView);
  const lifecycleLabel = lifecycle ? meetingLifecycleLabel(meeting, lifecycle) : '';
  const operationalCohostEmails = operationalState?.cohost_emails ?? [];

  return (
    <Layout>
      <div className="meeting-detail-nav"><button className="button button-ghost button-small" onClick={() => navigate('/dashboard')}>← Utama</button></div>
      <div className="meeting-detail-head">
        <div>
          <div className="eyebrow">{isHost ? 'Mesyuarat anjuran anda' : 'Mesyuarat anda'}</div>
          <h1>{meeting.title}</h1>
          <p className="muted">{formatDate(meeting.meeting_date)} · {formatTime(meeting.meeting_time)} · Dihoskan oleh {meeting.host_email}</p>
          <div className="meeting-context-row">
            {meeting.location && <span>📍 {meeting.location}</span>}
            {meeting.chairman && <span>Pengerusi: {meeting.chairman}</span>}
          </div>
          <div className="meeting-viewer-pills">
            {isHost && <MeetingRelationBadge relationship="HOST" />}
            {isCohost && <MeetingRelationBadge relationship="COHOST" />}
            {!isHost && !isCohost && userState.viewer.is_expected && <MeetingRelationBadge relationship="EXPECTED" />}
            {hasCheckedIn && <MeetingRelationBadge relationship="ATTENDED" />}
            {attendanceInvalidated && <MeetingRelationBadge relationship="INVALIDATED" />}
          </div>
        </div>
        <div className="meeting-head-actions">
          <span className={`status-pill ${lifecycle === 'CANCELLED' ? 'status-cancelled' : 'status-active'}`}>{lifecycleLabel}</span>
          {checkInOpen && !hasCheckedIn && !attendanceInvalidated && <button className="button button-primary" onClick={() => navigate(`/checkin/${meetingId}`)}>✓ {isHost ? 'Check-in Saya' : 'Check-in'}</button>}
          {checkInOpen && attendanceInvalidated && <button className="button button-secondary" disabled>Kehadiran Dibatalkan</button>}
          {checkInOpen && hasCheckedIn && <button className="button button-secondary" disabled>✓ Anda telah hadir</button>}
        </div>
      </div>

      <MeetingPeople hostEmail={meeting.host_email} currentCohosts={userState.current_cohosts} operationalCohostEmails={operationalCohostEmails} canRemove={isHost && Boolean(actionPolicy?.cohostMutation) && Boolean(operationalState)} onRemove={(email) => setCohostRemoval({ email, expected: operationalState?.expected.find((item) => item.name_or_email.toLowerCase() === email.toLowerCase()) ?? null, hasAttendanceHistory: Boolean(operationalState?.attendance.some((record) => record.user_email.toLowerCase() === email.toLowerCase())) })} />

      {canAttendanceExport && (
        <div className="attendance-tools" aria-label="Alat kehadiran">
          <div>
            <strong>Rekod kehadiran</strong>
            <span className="muted">Gunakan akses senarai kehadiran yang telah diberikan kepada akaun ini.</span>
          </div>
          <div className="action-row">
            <button type="button" className="button button-secondary" onClick={exportAttendanceCsv}>Eksport CSV</button>
            {canGenerateVerificationPrint ? <button type="button" className="button button-secondary" onClick={() => void exportAttendancePdf()} disabled={pdfBusy || printBusy}>{pdfBusy ? 'Menyediakan PDF…' : 'Eksport PDF'}</button> : null}
            {canGenerateVerificationPrint ? <button type="button" className="button button-primary" onClick={() => void printAttendance()} disabled={printBusy || pdfBusy}>{printBusy ? 'Menyediakan cetakan…' : 'Cetak Kehadiran'}</button> : null}
          </div>
        </div>
      )}

      {!checkInOpen && meeting.status === 'Active' && (
        <div className="meeting-window-note">
          {dayState === 'future' ? `Check-in dibuka pada ${formatDate(meeting.meeting_date)}.` : `Check-in ditutup selepas ${formatDate(meeting.meeting_date)}.`}
          {' '}Butiran mesyuarat dan senarai kehadiran kekal tersedia untuk anda.
        </div>
      )}

      {canManage && hostState && (
        <section className="host-hero-grid">
          <article className="qr-card">
            {canUseSelfCheckInQr
              ? qrDataUrl ? <img src={qrDataUrl} alt="Kod QR check-in mesyuarat" /> : <div className="qr-placeholder"><Spinner /></div>
              : <div className="qr-placeholder"><span>QR check-in tidak tersedia</span></div>}
            <div><strong>QR Kehadiran Stabil</strong><p>QR ini mengandungi ID mesyuarat yang kekal dan tidak berubah. Kehadiran hanya diterima pada tarikh yang dijadualkan.</p>{hostState.meeting.join_code && <div className="host-join-code"><span>Kod Mesyuarat</span><b>{hostState.meeting.join_code}</b><small>Tetamu boleh memasukkan kod ini di {window.location.host}.</small></div>}</div>
          </article>
          <article className="host-actions-card">
            <div className="host-stat"><strong>{validAttendanceCount}</strong><span>Check-in sah</span>{invalidAttendanceCount > 0 && <small>{invalidAttendanceCount} rekod audit dibatalkan</small>}</div>
            <div className="action-grid">
              {checkInOpen && !hasCheckedIn && !attendanceInvalidated && <button className="button button-primary" onClick={() => navigate(`/checkin/${meetingId}`)}>✓ Check-in Saya</button>}
              {checkInOpen && attendanceInvalidated && <button className="button button-secondary" disabled>Kehadiran Dibatalkan</button>}
              {checkInOpen && hasCheckedIn && <button className="button button-secondary" disabled>✓ Anda telah hadir</button>}
              <button className="button button-secondary" onClick={startEdit} disabled={!actionPolicy?.editMeeting}>Edit Mesyuarat</button>
              <button className="button button-secondary" onClick={openProjector} disabled={!actionPolicy?.projector}>Buka Paparan Projector</button>
              <button className="button button-secondary" onClick={saveQr} disabled={!canUseSelfCheckInQr}>Simpan Imej QR</button>
              <button className="button button-secondary" onClick={() => setManualContext({})} disabled={!actionPolicy?.manualCheckIn}>Check-in Manual</button>
              <button className="button button-warning" onClick={startCancel} disabled={!actionPolicy?.cancelMeeting}>Batalkan Mesyuarat</button>
              {!meeting.series_id ? <details className="danger-actions"><summary>Lebih banyak tindakan</summary><button type="button" className="button button-danger" onClick={remove} disabled={!actionPolicy?.deleteMeeting}>Padam Mesyuarat</button></details> : null}
            </div>
            <p className="tiny-note">E-mel MTIB yang disahkan sepadan dengan e-mel Host yang direkodkan. Kawalan ini boleh digunakan dari mana-mana peranti; hash keupayaan lama yang dikekalkan bukan autorisasi dalam aplikasi ini.</p>
          </article>
        </section>
      )}

      {isCohost && operationalState && (
        <section className="host-hero-grid cohost-operations-grid">
          <article className="host-actions-card">
            <div className="host-stat"><strong>{operationalState.attendance.filter((record) => record.is_valid !== false).length}</strong><span>Check-in sah</span></div>
            <div className="action-grid">
              <button className="button button-secondary" onClick={openProjector} disabled={!actionPolicy?.projector}>🖥️ Buka Paparan Projector</button>
              <button className="button button-secondary" onClick={saveQr} disabled={!canUseSelfCheckInQr}>📥 Simpan Imej QR</button>
              <button className="button button-secondary" onClick={() => setManualContext({})} disabled={!actionPolicy?.manualCheckIn}>✍️ Check-in Manual</button>
            </div>
            <p className="tiny-note">Akses operasi <em>Co-Host</em>: Paparan Projector, Check-in manual dan pembetulan kehadiran. Struktur mesyuarat dan keahlian <em>Co-Host</em> hanya boleh diurus oleh <em>Host</em>.</p>
          </article>
        </section>
      )}

      <section className="section-block">
        <div className="section-toolbar roster-toolbar">
          <div><h2>Senarai Kehadiran</h2><p className="muted">Rekod yang dibatalkan kekal untuk audit Host.</p></div>
          <div className="roster-filter-toolbar" aria-label="Tapisan senarai kehadiran">
            <label className="roster-search-field" htmlFor="roster-search"><span>Carian</span><input id="roster-search" className="search-input" type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Cari nama / bahagian…" aria-label="Cari nama atau bahagian" /></label>
            <label className="roster-category-field" htmlFor="roster-category"><span>Kategori</span><select id="roster-category" value={tab} onChange={(event) => setTab(event.target.value as typeof tab)} aria-label="Kategori kehadiran">
              <option value="All">Semua</option>
              <option value="Internal">Dalaman</option>
              <option value="Guest">Tetamu</option>
            </select></label>
            <label className="roster-sort-field" htmlFor="roster-sort"><span>Susunan</span><select id="roster-sort" value={sortMode} onChange={(event) => setSortMode(event.target.value as typeof sortMode)}>
              <option value="recent">Aktiviti Terkini</option>
              <option value="status">Status</option>
            </select></label>
            <button type="button" className="button button-secondary button-small" onClick={resetRosterFilters}>Reset</button>
          </div>
        </div>
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Nama / Ahli</th><th>Kategori</th><th>Status</th><th>Kaedah</th><th>Waktu Check-in</th>{canViewAttendanceAudit && <th>Tindakan</th>}{canViewAttendanceAudit && <th>Pembetulan audit</th>}</tr></thead>
            <tbody>{filteredRoster.length ? filteredRoster.map((row) => (
              <tr key={row.key}>
                <td><strong>{row.display}</strong><small>{row.sub}</small>{row.attendance?.corrections?.map((correction) => <small key={correction.correction_id}>{correction.action === 'INVALIDATE' ? `Dibatalkan: ${correction.reason}` : 'Dipulihkan'} oleh {correction.actor_email} · {formatDateTime(correction.created_at)}{correction.note ? ` · ${correction.note}` : ''}</small>)}</td>
                <td><span className="category-badge">{rosterCategoryLabel(row.category)}</span></td>
                <td><span className={`roster-status ${row.status === 'Checked In' ? 'checked' : row.status === 'Invalidated' ? 'invalid' : row.status === 'Not Coming' ? 'not-coming' : 'pending'}`}>{rosterStatusLabel(row.status)}{row.reason ? ` · ${row.reason}` : ''}{row.reasonDetail ? ` — ${row.reasonDetail}` : ''}</span></td>
                <td>{rosterMethodLabel(row.method)}</td>
                <td>{row.time ? formatDateTime(row.time) : '—'}</td>
                {canViewAttendanceAudit && <td><div className="roster-action-stack">
                  {row.expected && row.status === 'Pending' ? <><button className="button button-secondary button-small" onClick={() => void startExpectedCheckIn(row)} disabled={!actionPolicy?.expectedAttendeeMutation}>Check-in</button><button className="button button-ghost button-small" onClick={() => setExpectedAction({ row })} disabled={!actionPolicy?.expectedAttendeeMutation}>Tidak Hadir</button>{!operationalCohostEmails.some((email) => email.toLowerCase() === row.expected?.name_or_email.toLowerCase()) ? <button className="button button-ghost button-small" onClick={() => void removeExpected(row)} disabled={!actionPolicy?.expectedAttendeeMutation}>Keluarkan</button> : null}</> : null}
                  {row.expected && row.status === 'Not Coming' ? <><button className="button button-secondary button-small" onClick={() => setExpectedAction({ row })} disabled={!actionPolicy?.expectedAttendeeMutation}>Pulihkan kepada Belum Hadir</button><button className="button button-ghost button-small" disabled title="Pulihkan kepada Belum Hadir sebelum mengeluarkan ahli ini">Keluarkan</button></> : null}
                </div></td>}
                {canViewAttendanceAudit && <td>{row.attendance && (row.attendance.is_valid === false ? <button className="button button-ghost button-small" onClick={() => void restoreAttendance(row.attendance!)} disabled={!actionPolicy?.attendanceCorrection}>Pulihkan</button> : <button className="button button-ghost button-small" onClick={() => setInvalidating(row.attendance!)} disabled={!actionPolicy?.attendanceCorrection}>Batalkan rekod</button>)}</td>}
              </tr>
            )) : <tr><td colSpan={canViewAttendanceAudit ? 7 : 5} className="table-empty">Tiada ahli sepadan dengan paparan ini.</td></tr>}</tbody>
          </table>
        </div>
      </section>

      {canGenerateVerificationPrint && verificationPrintDocument && <PrintAttendanceContent
        printDocument={verificationPrintDocument}
        verificationQrDataUrl={verificationQrDataUrl}
      />}

      {seriesAction && meeting.series_id ? <SeriesActionScopeModal
        action={seriesAction}
        date={meeting.meeting_date}
        onClose={() => setSeriesAction(null)}
        onContinue={(scope) => {
          if (seriesAction === 'edit') navigate(`/meetings/${meetingId}/edit?scope=${scope}`);
          else cancel(scope);
        }}
      /> : null}
      {manualContext && canOperate && actionPolicy?.manualCheckIn && <ManualCheckInModal meetingId={meetingId} expectedRow={manualContext.row} registeredProfile={manualContext.registeredProfile} onClose={() => setManualContext(null)} onSaved={() => load(true)} />}
      {expectedAction && canOperate && actionPolicy?.expectedAttendeeMutation ? <ExpectedAttendeeActionModal row={expectedAction.row} onClose={() => setExpectedAction(null)} onSaved={async () => { setExpectedAction(null); await load(true); }} /> : null}
      {cohostRemoval && isHost && actionPolicy?.cohostMutation ? <CohostRemovalModal meetingId={meetingId} email={cohostRemoval.email} expected={cohostRemoval.expected} hasAttendanceHistory={cohostRemoval.hasAttendanceHistory} hasSeries={Boolean(meeting.series_id)} onClose={() => setCohostRemoval(null)} onSaved={async () => { setCohostRemoval(null); await load(true); }} /> : null}
      {cancelDialog && isHost && actionPolicy?.cancelMeeting ? <CancelMeetingModal meetingId={meetingId} seriesId={meeting.series_id ?? null} scope={cancelDialog.scope} attendanceCount={validAttendanceCount} onClose={() => setCancelDialog(null)} onSaved={async () => { setCancelDialog(null); await load(true); }} /> : null}
      {invalidating && canOperate && actionPolicy?.attendanceCorrection && <InvalidateAttendanceModal record={invalidating} onClose={() => setInvalidating(null)} onSaved={async (reason, note) => {
        await correctAttendanceAsStaff({ recordId: invalidating.record_id, action: 'INVALIDATE', reason, note });
        showToast('Rekod kehadiran dibatalkan. Rekod asal tidak berubah.', 'success');
        setInvalidating(null);
        await load(true);
      }} />}
    </Layout>
  );
}

function PrintAttendanceContent({
  printDocument,
  verificationQrDataUrl,
}: {
  printDocument: AttendanceVerificationPrintDocument;
  verificationQrDataUrl: string;
}) {
  return <section className="print-attendance" aria-labelledby="print-attendance-title">
    <article className="print-meeting-card">
      <div className="print-meeting-main">
        <h1 id="print-attendance-title">{printDocument.title}</h1>
        <dl>
          <div><dt>Tarikh</dt><dd>{formatDate(printDocument.meeting_date)}</dd></div>
          <div><dt>Waktu</dt><dd>{formatTime(printDocument.meeting_time)}</dd></div>
          {printDocument.chairman && <div><dt>Pengerusi</dt><dd>{printDocument.chairman}</dd></div>}
          {printDocument.location && <div><dt>Lokasi</dt><dd>{printDocument.location}</dd></div>}
        </dl>
      </div>
      <figure className="print-verification-qr">
        {verificationQrDataUrl ? <img src={verificationQrDataUrl} alt="Kod QR pengesahan elektronik rekod kehadiran" /> : <div className="print-qr-placeholder" aria-label="Kod QR pengesahan tidak tersedia" />}
        <figcaption>Imbas QR untuk Pengesahan Elektronik Kehadiran</figcaption>
      </figure>
    </article>
    <h2>Senarai Kehadiran</h2>
    <table>
      <thead><tr><th>No.</th><th>Nama</th><th>Bahagian / Organisasi</th><th>Status</th><th>Check-in</th></tr></thead>
      <tbody>{printDocument.roster.length ? printDocument.roster.map((row, index) => (
        <tr key={row.key}>
          <td>{index + 1}</td>
          <td>{row.name}</td>
          <td>{row.department_org || '—'}</td>
          <td>{formatOfficialAttendanceStatus(row)}</td>
          <td>{row.checkin_time ? formatMalaysiaIsoTime(row.checkin_time) : '—'}</td>
        </tr>
      )) : <tr><td colSpan={5}>Tiada rekod senarai kehadiran.</td></tr>}</tbody>
    </table>
    <dl className="print-provenance" aria-label="Maklumat penjanaan dokumen">
      <div><dt>Kod Rujukan</dt><dd>{printDocument.provenance.reference_code}</dd></div>
      <div><dt>Dijana pada</dt><dd>{formatDateTime(printDocument.provenance.generated_at)}</dd></div>
      <div><dt>Dijana oleh</dt><dd>{printDocument.provenance.generated_by_name}</dd></div>
      <div><dt>Bahagian</dt><dd>{printDocument.provenance.generated_by_department}</dd></div>
    </dl>
    <p className="print-document-footer">Dokumen ini telah dijana oleh komputer. Tandatangan adalah tidak diperlukan. Pengesahan kesahihan dokumen pada QR di atas.</p>
  </section>;
}

function MeetingPeople({ hostEmail, currentCohosts, operationalCohostEmails, canRemove, onRemove }: { hostEmail: string; currentCohosts: SafeCohostDisplay[]; operationalCohostEmails: string[]; canRemove: boolean; onRemove: (email: string) => void }) {
  const [names, setNames] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const managedCohostEmails = canRemove ? operationalCohostEmails : [];
  const people = useMemo(() => [hostEmail, ...managedCohostEmails], [hostEmail, managedCohostEmails]);
  const peopleKey = people.map((email) => email.toLowerCase()).join('|');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void Promise.all(people.map((email) => findUserDirectoryUser(email))).then((profiles) => {
      if (cancelled) return;
      const next: Record<string, string> = {};
      profiles.forEach((profile, index) => {
        if (profile) next[people[index].toLowerCase()] = profile.full_name;
      });
      setNames(next);
      setLoading(false);
    }).catch(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [peopleKey]);

  const displayName = (email: string) => names[email.toLowerCase()] ?? (loading ? 'Memuatkan…' : 'Kakitangan MTIB');
  return <section className="meeting-people-card" aria-label="Pihak mesyuarat">
    <div><span className="meeting-people-label"><em>Host</em></span><strong>{displayName(hostEmail)}</strong></div>
    <div><span className="meeting-people-label"><em>Co-Host</em></span>{canRemove ? managedCohostEmails.map((email) => <div className="meeting-person-row" key={email}><strong>{displayName(email)}</strong><button type="button" className="button button-ghost button-small" onClick={() => onRemove(email)}>Keluarkan</button></div>) : currentCohosts.map((cohost, index) => <div className="meeting-person-row" key={`${cohost.display_name}-${index}`}><strong>{cohost.display_name}</strong></div>)}{(canRemove ? managedCohostEmails : currentCohosts).length === 0 ? <span className="muted">Tiada</span> : null}</div>
  </section>;
}

function SeriesActionScopeModal({
  action,
  date,
  onClose,
  onContinue,
}: {
  action: 'edit' | 'cancel';
  date: string;
  onClose: () => void;
  onContinue: (scope: 'occurrence' | 'series') => void;
}) {
  const [scope, setScope] = useState<'occurrence' | 'series'>('occurrence');
  const verb = action === 'edit' ? 'Edit' : 'Batalkan';
  return <Modal title={`${verb} mesyuarat`} onClose={onClose}>
    <div className="form-stack">
      <div className="scope-options" role="radiogroup" aria-label={`Skop ${verb.toLowerCase()}`}>
        <label className={`scope-option${scope === 'occurrence' ? ' selected' : ''}`}>
          <input type="radio" name={`${action}-scope`} checked={scope === 'occurrence'} onChange={() => setScope('occurrence')} />
          <span><strong>Hari ini sahaja</strong><small>{action === 'edit' ? `Ubah ${formatDate(date)} sahaja` : `Batalkan ${formatDate(date)} sahaja`}</small></span>
        </label>
        <label className={`scope-option${scope === 'series' ? ' selected' : ''}`}>
          <input type="radio" name={`${action}-scope`} checked={scope === 'series'} onChange={() => setScope('series')} />
          <span><strong>Seluruh siri</strong><small>{action === 'edit' ? 'Ubah semua hari dalam siri mesyuarat ini' : 'Batalkan semua mesyuarat yang berbaki dalam siri ini'}</small></span>
        </label>
      </div>
      <div className="form-actions"><button type="button" className="button button-ghost" onClick={onClose}>{action === 'cancel' ? 'Kembali' : 'Batal'}</button><button type="button" className={action === 'cancel' ? 'button button-warning' : 'button button-primary'} onClick={() => onContinue(scope)}>Teruskan</button></div>
    </div>
  </Modal>;
}

function ManualCheckInModal({ meetingId, expectedRow, registeredProfile, onClose, onSaved }: {
  meetingId: string;
  expectedRow?: DisplayRosterRow;
  registeredProfile?: DirectoryUser | null;
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) {
  const expectedValue = expectedRow?.expected?.name_or_email.trim() ?? '';
  const expectedEmail = isValidEmail(expectedValue) ? expectedValue.toLowerCase() : '';
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<DirectoryUser[]>([]);
  const [selected, setSelected] = useState<DirectoryUser | null>(registeredProfile ?? null);
  const [manualEntry, setManualEntry] = useState(Boolean(expectedRow && !registeredProfile));
  const [email, setEmail] = useState(registeredProfile?.email ?? expectedEmail);
  const [fullName, setFullName] = useState(registeredProfile?.full_name ?? (expectedEmail ? '' : expectedValue));
  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const [organisation, setOrganisation] = useState(registeredProfile?.department_org ?? '');
  const [departments, setDepartments] = useState<Department[]>([]);
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();

  useEffect(() => {
    void getDepartments().then(setDepartments).catch(() => undefined);
  }, []);
  useEffect(() => {
    if (!expectedRow || selected || !isValidEmail(email)) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const profile = await findUserDirectoryUser(email);
        if (!cancelled && profile) choose(profile);
      } catch (error) {
        if (!cancelled) showToast(errorMessage(error), 'error');
      }
    }, 260);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [email, expectedRow, selected, showToast]);
  useEffect(() => {
    if (selected || query.trim().length < 3) { setResults([]); return; }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      try {
        const matches = await searchUserDirectory(query);
        if (!cancelled) setResults(matches);
      } catch (error) {
        if (!cancelled) showToast(errorMessage(error), 'error');
      }
    }, 260);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [query, selected, showToast]);

  const choose = (user: DirectoryUser) => {
    setSelected(user);
    setManualEntry(false);
    setQuery('');
    setEmail(user.email);
    setFullName(user.full_name);
    setOrganisation(user.department_org ?? '');
    setDepartmentId(null);
    setResults([]);
  };
  const enterManually = () => {
    setSelected(null);
    setManualEntry(true);
    setEmail(query.trim().toLowerCase());
    setFullName('');
    setOrganisation('');
    setDepartmentId(null);
    setResults([]);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!isValidEmail(normalizedEmail)) { showToast('Sila masukkan e-mel ahli yang sah.', 'error'); return; }
    if (fullName.trim().length < 2) { showToast('Sila masukkan nama penuh ahli.', 'error'); return; }
    if (!selected && isMtibEmail(normalizedEmail) && departmentId === null) { showToast('Pilih bahagian ahli daripada senarai bahagian rasmi.', 'error'); return; }
    if (!selected && !isMtibEmail(normalizedEmail) && organisation.trim().length < 2) { showToast('Sila masukkan organisasi luaran.', 'error'); return; }
    setBusy(true);
    try {
      const input = {
        meetingId,
        email: normalizedEmail,
        fullName: normalizeProfileText(fullName),
        departmentId: selected ? null : departmentId,
        organisation: selected ? '' : organisation.trim(),
      };
      const result = expectedRow?.expected
        ? await manualCheckInExpectedAttendee({ ...input, expectedId: expectedRow.expected.expected_id })
        : await manualCheckInMyHostedMeeting(input);
      showToast(result.already_checked_in ? 'Ahli ini telah membuat Check-in.' : 'Kehadiran manual telah direkodkan.', result.already_checked_in ? 'info' : 'success');
      await onSaved();
      onClose();
    } catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setBusy(false); }
  };

  const showForm = Boolean(selected || manualEntry);
  const internal = isValidEmail(email) && isMtibEmail(email);
  const emailLocked = Boolean(selected || expectedEmail);
  return <Modal title="Check-in Manual" onClose={onClose}><form className="form-stack" onSubmit={submit}>
    {!expectedRow ? <><label className="field"><span>Cari ahli</span><input value={selected ? selected.full_name : query} onChange={(event) => { setSelected(null); setManualEntry(false); setQuery(event.target.value); setEmail(''); }} placeholder="Taip nama atau e-mel…" autoComplete="off" autoFocus /></label>
      {!selected && query.trim().length >= 3 ? <div className="directory-results manual-checkin-results">{results.length ? results.map((user) => <button type="button" className="directory-result" key={user.email} onMouseDown={(event) => event.preventDefault()} onClick={() => choose(user)}><strong>{user.full_name}</strong><span>{user.email} · {user.department_org ?? 'Bahagian tidak disenaraikan'}</span></button>) : <><div className="directory-message">Tiada orang berdaftar ditemui.</div>{isValidEmail(query.trim()) ? <button type="button" className="button button-secondary manual-entry-button" onClick={enterManually}>Masukkan secara manual</button> : null}</>}</div> : null}</> : null}
    {selected ? <div className="selected-identity"><strong>{selected.full_name}</strong><span>{selected.email} · {selected.department_org ?? 'Bahagian tidak disenaraikan'}</span><small>Identiti berdaftar dipilih. Nama dan bahagian adalah sumber rasmi.</small></div> : null}
    {showForm && <>
      <label className="field"><span>E-mel</span><input type="email" value={email} onChange={(event) => { setEmail(event.target.value.toLowerCase()); setDepartmentId(null); setOrganisation(''); }} disabled={emailLocked} required autoFocus={Boolean(expectedRow && !expectedEmail)} /></label>
      <label className="field"><span>Nama penuh</span><input value={fullName} onChange={(event) => setFullName(event.target.value)} disabled={Boolean(selected)} required /></label>
      {!selected && isValidEmail(email) ? <div className="selected-identity"><span>Klasifikasi</span><strong>{internal ? 'Warga MTIB' : 'Tetamu'}</strong><small>Klasifikasi ditentukan secara automatik daripada domain e-mel.</small></div> : null}
      {!selected && internal ? <label className="field"><span>Bahagian</span><select value={departmentId ?? ''} onChange={(event) => setDepartmentId(event.target.value ? Number(event.target.value) : null)} required><option value="">Pilih bahagian</option>{departments.map((department) => <option key={department.department_id} value={department.department_id}>{department.department_name}</option>)}</select></label> : null}
      {!selected && isValidEmail(email) && !internal ? <label className="field"><span>Organisasi</span><input value={organisation} onChange={(event) => setOrganisation(event.target.value)} required /></label> : null}
    </>}
    <small className="muted">{expectedRow ? 'Kehadiran akan direkodkan sebagai Check-in manual. ' : ''}Check-in manual hanya merekodkan salinan sejarah kehadiran. Ia tidak mencipta akaun pengguna atau identiti Supabase Auth.</small>
    <div className="form-actions"><button type="button" className="button button-ghost" onClick={onClose}>Batal</button><button className="button button-primary" disabled={busy || !showForm}>{busy ? 'Menyimpan…' : expectedRow ? 'Sahkan Check-in' : 'Rekod Check-in ahli'}</button></div>
  </form></Modal>;
}

function ExpectedAttendeeActionModal({ row, onClose, onSaved }: { row: DisplayRosterRow; onClose: () => void; onSaved: () => Promise<void>; }) {
  const expected = row.expected;
  const [reason, setReason] = useState('MEETING');
  const [detail, setDetail] = useState('');
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();
  if (!expected) return null;
  const restoring = row.status === 'Not Coming';
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!restoring && reason === 'OTHER' && !detail.trim()) { showToast('Sila masukkan maklumat tambahan bagi Lain-lain.', 'error'); return; }
    setBusy(true);
    try {
      await setExpectedAttendeeDisposition({ expectedId: expected.expected_id, status: restoring ? 'PENDING' : 'NOT_COMING', reasonCode: restoring ? null : reason, reasonDetail: restoring ? null : detail.trim() || null });
      showToast(restoring ? 'Ahli telah dipulihkan kepada Belum Hadir.' : 'Ahli ditandakan sebagai Tidak Hadir.', 'success');
      await onSaved();
    } catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setBusy(false); }
  };
  return <Modal title={restoring ? 'Pulihkan ahli' : 'Tandakan Tidak Hadir'} onClose={onClose}>
    <form className="form-stack" onSubmit={submit}>
      <div className="selected-identity"><strong>{row.display}</strong><span>{row.departmentOrg ?? expected.name_or_email}</span></div>
      {!restoring ? <><label className="field"><span>Sebab</span><select value={reason} onChange={(event) => setReason(event.target.value)}><option value="MEETING">Mesyuarat</option><option value="OUTSTATION">Tugas Luar</option><option value="ANNUAL_LEAVE">Cuti Rehat</option><option value="SICK_LEAVE">Cuti Sakit</option><option value="OTHER">Lain-lain</option></select></label><label className="field"><span>Maklumat tambahan {reason === 'OTHER' ? <em>(Wajib)</em> : <em>(Pilihan)</em>}</span><textarea value={detail} onChange={(event) => setDetail(event.target.value)} maxLength={500} rows={3} required={reason === 'OTHER'} /></label></> : null}
      <div className="form-actions"><button type="button" className="button button-ghost" onClick={onClose}>Batal</button><button className="button button-warning" disabled={busy}>{busy ? 'Menyimpan…' : restoring ? 'Pulihkan kepada Belum Hadir' : 'Tandakan Tidak Hadir'}</button></div>
    </form>
  </Modal>;
}

function CohostRemovalModal({ meetingId, email, expected, hasAttendanceHistory, hasSeries, onClose, onSaved }: { meetingId: string; email: string; expected: ExpectedAttendee | null; hasAttendanceHistory: boolean; hasSeries: boolean; onClose: () => void; onSaved: () => Promise<void>; }) {
  const [choice, setChoice] = useState<'both' | 'attendee' | 'cohost'>('both');
  const [scope, setScope] = useState<'OCCURRENCE' | 'SERIES'>('OCCURRENCE');
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();
  const removable = expected?.effective_status === 'PENDING';
  const protectedHistory = hasAttendanceHistory || Boolean(expected && !removable);
  const submit = async () => {
    setBusy(true);
    try {
      await removeMeetingCohost({ meetingId, email, removeExpected: choice === 'both', scope });
      showToast(choice === 'both' ? 'Co-Host dan rekod ahli dijemput yang layak telah dikeluarkan.' : 'Co-Host telah dikeluarkan.', 'success');
      await onSaved();
    } catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setBusy(false); }
  };
  return <Modal title="Keluarkan Co-Host" onClose={onClose}>
    <div className="form-stack">
      {protectedHistory ? <><p>Orang ini mempunyai sejarah kehadiran dan akan kekal dalam senarai kehadiran.</p><div className="form-actions"><button type="button" className="button button-ghost" onClick={onClose}>Batal</button><button type="button" className="button button-warning" onClick={() => { setChoice('cohost'); void submit(); }} disabled={busy}>{busy ? 'Mengeluarkan…' : 'Keluarkan Co-Host'}</button></div></> : <><p>Orang ini juga merupakan ahli dijemput. Apakah tindakan yang anda mahu?</p>{hasSeries ? <label className="field"><span>Gunakan pada</span><select value={scope} onChange={(event) => setScope(event.target.value as typeof scope)}><option value="OCCURRENCE">Hari ini sahaja</option><option value="SERIES">Seluruh siri</option></select></label> : null}<div className="choice-list"><label><input type="radio" checked={choice === 'both'} onChange={() => setChoice('both')} /><span>Keluarkan kedua-duanya</span></label><label><input type="radio" checked={choice === 'attendee'} onChange={() => setChoice('attendee')} /><span>Kekalkan sebagai ahli</span></label></div><div className="form-actions"><button type="button" className="button button-ghost" onClick={onClose}>Batal</button><button type="button" className="button button-warning" onClick={() => void submit()} disabled={busy}>{busy ? 'Mengeluarkan…' : choice === 'both' ? 'Keluarkan kedua-duanya' : 'Keluarkan Co-Host'}</button></div></>}
    </div>
  </Modal>;
}

function CancelMeetingModal({ meetingId, seriesId, scope, attendanceCount, onClose, onSaved }: { meetingId: string; seriesId: string | null; scope: 'occurrence' | 'series'; attendanceCount: number; onClose: () => void; onSaved: () => Promise<void>; }) {
  const [reason, setReason] = useState('CHAIRMAN_UNAVAILABLE');
  const [detail, setDetail] = useState('');
  const [releaseRooms, setReleaseRooms] = useState(true);
  const [seriesScope, setSeriesScope] = useState<'ALL_ACTIVE' | 'FROM_SELECTED'>('FROM_SELECTED');
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();
  const wholeSeries = scope === 'series';
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (reason === 'OTHER' && !detail.trim()) { showToast('Sila masukkan maklumat tambahan bagi sebab Lain-lain.', 'error'); return; }
    setBusy(true);
    try {
      if (wholeSeries) {
        if (!seriesId) throw new Error('Siri mesyuarat tidak tersedia.');
        await cancelMyHostedMeetingSeries(seriesId, meetingId, seriesScope, reason, detail.trim() || null);
      } else {
        await cancelMyHostedMeeting(meetingId, reason, detail.trim() || null, releaseRooms);
      }
      showToast(wholeSeries ? 'Siri mesyuarat telah dibatalkan.' : 'Mesyuarat telah dibatalkan. Sejarah kehadiran dikekalkan.', 'success');
      await onSaved();
    } catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setBusy(false); }
  };
  return <Modal title={wholeSeries ? 'Batalkan Seluruh Siri' : 'Batalkan Mesyuarat'} onClose={onClose}><form className="form-stack" onSubmit={submit}>
    <label className="field"><span>Sebab</span><select value={reason} onChange={(event) => setReason(event.target.value)}><option value="CHAIRMAN_UNAVAILABLE">Pengerusi tidak tersedia</option><option value="POSTPONED">Mesyuarat ditangguhkan</option><option value="QUORUM_NOT_MET">Kuorum tidak mencukupi</option><option value="OTHER">Lain-lain</option></select></label>
    <label className="field"><span>Maklumat tambahan {reason === 'OTHER' ? <em>(Wajib)</em> : <em>(Pilihan)</em>}</span><textarea value={detail} onChange={(event) => setDetail(event.target.value)} maxLength={1000} rows={3} required={reason === 'OTHER'} /></label>
    {wholeSeries ? <div className="field"><span>Skop pembatalan siri</span><div className="scope-options" role="radiogroup" aria-label="Skop pembatalan siri">
      <label className={`scope-option${seriesScope === 'ALL_ACTIVE' ? ' selected' : ''}`}><input type="radio" name="series-cancellation-scope" checked={seriesScope === 'ALL_ACTIVE'} onChange={() => setSeriesScope('ALL_ACTIVE')} /><span><strong>Semua mesyuarat dalam siri</strong><small>Batalkan semua mesyuarat yang masih aktif dalam siri ini, termasuk mesyuarat terdahulu.</small></span></label>
      <label className={`scope-option${seriesScope === 'FROM_SELECTED' ? ' selected' : ''}`}><input type="radio" name="series-cancellation-scope" checked={seriesScope === 'FROM_SELECTED'} onChange={() => setSeriesScope('FROM_SELECTED')} /><span><strong>Mesyuarat ini dan seterusnya</strong><small>Batalkan mesyuarat yang sedang dipilih dan semua mesyuarat selepasnya. Mesyuarat terdahulu akan dikekalkan.</small></span></label>
    </div></div> : null}
    {!wholeSeries && attendanceCount > 0 ? <p className="warning-note">{attendanceCount} orang telah membuat Check-in. Rekod kehadiran mereka akan dikekalkan.</p> : null}
    {!wholeSeries ? <label className="release-room-choice"><input type="checkbox" checked={releaseRooms} onChange={(event) => setReleaseRooms(event.target.checked)} /><span><strong>Lepaskan tempahan bilik</strong><small>Bilik yang dilepaskan boleh ditempah semula. Bukti sejarah tempahan kekal disimpan.</small></span></label> : null}
    {wholeSeries ? <p className="muted">Tempahan bilik bagi semua mesyuarat yang dibatalkan akan dilepaskan secara automatik. Jika anda ingin mengekalkan tempahan bilik bagi mana-mana mesyuarat, sila batalkan mesyuarat tersebut secara individu melalui pilihan “Hari ini sahaja”.</p> : null}
    <p className="warning-note">Pembatalan mesyuarat adalah muktamad dan tidak boleh dipulihkan. Sila pastikan butiran pembatalan adalah tepat. Rekod kehadiran sedia ada akan dikekalkan.</p>
    <div className="form-actions"><button type="button" className="button button-ghost" onClick={onClose}>Kekalkan Mesyuarat</button><button className="button button-warning" disabled={busy}>{busy ? 'Membatalkan…' : wholeSeries ? 'Sahkan pembatalan' : 'Batalkan Mesyuarat'}</button></div>
  </form></Modal>;
}

function InvalidateAttendanceModal({ record, onClose, onSaved }: { record: AttendanceRecord; onClose: () => void; onSaved: (reason: 'Accidental scan' | 'Wrong meeting' | 'Test entry' | 'Other', note: string) => Promise<void>; }) {
  const [reason, setReason] = useState<'Accidental scan' | 'Wrong meeting' | 'Test entry' | 'Other'>('Accidental scan');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const { showToast } = useToast();

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (reason === 'Other' && !note.trim()) {
      showToast('Masukkan nota penerangan apabila sebabnya Lain-lain.', 'error');
      return;
    }
    setBusy(true);
    try { await onSaved(reason, note.trim()); }
    catch (error) { showToast(errorMessage(error), 'error'); }
    finally { setBusy(false); }
  };

  return <Modal title="Batalkan Kehadiran" onClose={onClose}><form className="form-stack" onSubmit={submit}>
    <p className="muted">Ini mencipta rekod audit baharu untuk <strong>{record.snapshot_name}</strong>. Rekod kehadiran asal tidak akan diubah atau dipadam.</p>
    <label className="field"><span>Sebab</span><select value={reason} onChange={(event) => setReason(event.target.value as typeof reason)}><option value="Accidental scan">Imbasan tersilap</option><option value="Wrong meeting">Mesyuarat tersilap</option><option value="Test entry">Entri ujian</option><option value="Other">Lain-lain</option></select></label>
    <label className="field"><span>Nota {reason === 'Other' ? <em>(Wajib bagi Lain-lain)</em> : <em>(Pilihan)</em>}</span><textarea value={note} onChange={(event) => setNote(event.target.value)} maxLength={1000} rows={3} required={reason === 'Other'} /></label>
    <div className="form-actions"><button type="button" className="button button-ghost" onClick={onClose}>Batal</button><button className="button button-danger" disabled={busy || (reason === 'Other' && !note.trim())}>{busy ? 'Menyimpan…' : 'Batalkan kehadiran'}</button></div>
  </form></Modal>;
}
